import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Quick add-to-cart for Aromas de Brisa buttons (`[data-ab-add]`).
 *
 * Mirrors the add flow in `product-form.js` so the cart drawer and cart count
 * update through the same `CartLinesUpdateEvent` contract. Each button sits in a
 * native `/cart/add` form, which keeps adding usable without JavaScript.
 */

/** @param {HTMLButtonElement} button @param {boolean} busy */
function setBusy(button, busy) {
  button.toggleAttribute('aria-busy', busy);
  button.disabled = busy;
}

/** @param {HTMLButtonElement} button */
async function addToCart(button) {
  const variantId = button.dataset.variantId;
  if (!variantId) return;

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');

  const sectionIds = [];
  for (const item of document.querySelectorAll('cart-items-component')) {
    if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
  }
  if (sectionIds.length) formData.set('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  setBusy(button, true);
  const label = button.querySelector('[data-ab-add-label]');
  const originalLabel = label?.textContent;

  try {
    const config = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...config,
      headers: { ...config.headers, Accept: 'text/html' },
    }).then((res) => res.json());

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());

    if (response.status) {
      button.dispatchEvent(
        new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'ab-add-to-cart' },
      });
      if (label) label.textContent = response.description || response.message;
    } else {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: 1,
          source: 'ab-add-to-cart',
          productId: button.dataset.productId,
          sections: response.sections,
        },
      });
      if (label) label.textContent = button.dataset.addedText || '✓';
      button.closest('dialog')?.close();
    }
  } catch (error) {
    deferred.reject(error);
    button.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    setBusy(button, false);
    if (label && originalLabel) setTimeout(() => (label.textContent = originalLabel), 2000);
  }
}

if (!window.abAddToCartReady) {
  window.abAddToCartReady = true;
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-ab-add]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    addToCart(button);
  });
}
